import { Component, Vue } from 'vue-property-decorator'
import AppForm from '@core/components/AppForm.vue'
import AppDatatable from '@core/components/AppDatatable.vue'
import AppDialog from '@core/components/AppDialog.vue'
import { inject } from 'inversify-props'
import { IDateService } from '@master/services/IDateService'
import { FormStructure } from '@master/model/structure/FormStructure'
import { camelCase } from 'lodash/fp'
import { CommissionRecordDTO, CommissionRecordsService } from './api'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { IProjectInfo } from '@master/config/IProjectInfo'
import { IProjectSettings } from '@master/config/IProjectSettings'
import CommissionDetailsDialogComponent from '@core/pages/CommissionModule/new-commission-module-components/CommissionDetailsDialogComponent.vue'

@Component({
  components: {
    AppDatatable,
    AppForm,
    AppDialog,
    CommissionDetailsDialogComponent
  }
})
export class CommissionDetailsMixin extends Vue {
  /* eslint-disable @typescript-eslint/camelcase */
  @inject()
  private dateService: IDateService

  @inject()
  private projectInfo: IProjectInfo

  @inject()
  private projectSettings: IProjectSettings

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

  private commissionBatchOid

  private txtSearchRecords = ''

  private detailsTableData: CommissionRecordDTO[] = []

  private showDetailDialog = false

  showFirstDeleteDialog = false
  showSecondDeleteDialog = false
  confirmText = ''

  private selectedDetail: CommissionRecordDTO = null

  private percentageKey: (keyof CommissionRecordDTO) [] = ['level1ComPct', 'level2ComPct', 'level3ComPct', 'level4ComPct', 'introducerComPct', 'vestingComPct', 'servicingComPct', 'clientRebatePct']
  private currencyKey: (keyof CommissionRecordDTO) [] = ['premiumAmt', 'commissionAmt', 'orCommissionAmt', 'level1ComAmt', 'level2ComAmt', 'level3ComAmt', 'level4ComAmt', 'introducerComAmt', 'vestingComAmt', 'servicingComAmt',
    'level1BscClawbackAmt', 'level2BscClawbackAmt', 'level3BscClawbackAmt', 'level4BscClawbackAmt', 'totalBscClawbackAmt', 'clientRebateAmt', 'companyRevenue', 'level1ComPayout', 'level2ComPayout', 'level3ComPayout', 'level4ComPayout']

  // extra columns like performance bonus payout will be addded only if config useHierarchySetup is true below
  private detailsRecordsColumn: Array<object> = [
    { label: 'Adviser Code', field: 'advCode' },
    { label: 'Adviser Name', field: 'advName' },
    { label: 'Policy Number', field: 'policyNumber' },
    { label: 'Client Name', field: 'clientName' },
    { label: 'Product Name', field: 'productName' },
    { label: 'Premium', field: 'premiumAmt' },
    { label: 'Comission', field: 'commissionAmt' },
    { label: 'OR Comission', field: 'orCommissionAmt' },
    { label: 'Transaction Type', field: 'transactionType' },
    { label: 'Level 1 Commission', field: 'level1ComAmt' },
    { label: 'Level 2 Commission', field: 'level2ComAmt' },
    { label: 'Level 3 Commission', field: 'level3ComAmt' },
    { label: 'Level 4 Commission', field: 'level4ComAmt' },
    { label: 'Introducer Commission', field: 'introducerComAmt' },
    { label: 'Vesting Commission', field: 'vestingComAmt' },
    { label: 'Servicing Commission', field: 'servicingComAmt' },
    { label: 'Paid Status', field: 'paid' }]

  get commissionDecimal () {
    return this.projectInfo.commissionDecimal ?? 2
  }

  get commissionInfo () {
    return this.detailsTableData.length > 0 ? Object.assign({}, this.detailsTableData[0]) : {
      paymentDateMillis: null,
      statementDateMillis: null,
      principal: null,
      remunerationType: null,
      paid: null,
      taxInvoiceNo: null,
      batchRemark: null
    }
  }

  get comissionInfoStructure (): FormStructure<CommissionRecordDTO> {
    return [
      {
        label: 'Payment Date',
        key: 'paymentDateMillis',
        type: 'date',
        disabled: true
      },
      {
        label: 'Statement Date',
        key: 'statementDateMillis',
        type: 'date',
        disabled: true
      },
      {
        label: 'Principal',
        key: 'principal',
        disabled: true
      },
      {
        label: 'Remuneration Type*',
        key: 'remunerationType',
        options: [
          { label: '', value: undefined },
          { label: 'Commission', value: 0 },
          { label: 'Trailer Fee', value: 1 },
          { label: 'Wrap Fee', value: 2 }
        ],
        disabled: true
      },
      {
        label: 'Paid',
        key: 'paid',
        options: [
          { label: '', value: 'unselected' },
          { label: 'Paid', value: 'paid' },
          { label: 'Unpaid', value: 'unpaid' }
        ]
      },
      {
        label: 'Tax Invoice No.*',
        key: 'taxInvoiceNo',
        disabled: true
      },
      {
        label: 'Remarks',
        key: 'batchRemark'
      }
    ]
  }

  formatValue (target, decimal) {
    if (target !== 0 && !target) {
      return ''
    }
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal
    })
    return formatter.format(target)
  }

  get detailsList () {
    return this.detailsTableData.map(records => {
      const paymentDateMillis = this.dateService.format(records.paymentDateMillis, 'YYYY-MM-DD')
      const statementDateMillis = this.dateService.format(records.statementDateMillis, 'YYYY-MM-DD')
      const issuedDateMillis = this.dateService.format(records.issuedDateMillis, 'YYYY-MM-DD')
      const paidToDate = this.dateService.format(records.paidToDate, 'YYYY-MM-DD')
      const keyword = Object.values(records).join(',').toLowerCase()
      const emit = true
      return {
        ...records,
        paymentDateMillis,
        statementDateMillis,
        issuedDateMillis,
        paidToDate,
        keyword,
        emit
      }
    })
  }

  get deleteButtonEnabled () {
    // only allow to delete when the paid status is unpaid
    return this.commissionInfo.paid === 'unpaid'
  }

  deleteRecord () {
    // two prompt message will be display
    // 1. to confirm what to delete the records with details
    // 2. prompt to key in value to confirm
    this.showFirstDeleteDialog = true
  }

  confirmDelete1 () {
    this.showFirstDeleteDialog = false
    this.showSecondDeleteDialog = true
  }

  resetDeleteDialog () {
    this.confirmText = ''
    this.showFirstDeleteDialog = false
    this.showSecondDeleteDialog = false
  }

  async submitDeleteText () {
    if (this.confirmText === 'CONFIRM DELETE') {
      try {
        const { success } = await CommissionRecordsService.deleteCommissionRecords(this.commissionBatchOid)
        if (success) {
          await this.uiService.alert.show({
            type: 'success',
            message: 'Successful delete.'
          })
          this.$router.push({ path: '/commission-module' })
        }
      } catch (error) {
        await this.uiService.alert.show({
          type: 'error',
          message: error
        })
      }
    } else {
      await this.uiService.alert.show({
        type: 'warning',
        message: 'The Input text not match'
      })
    }
  }

  async created () {
    // take injected UIService and set it to local
    this.uiService = this.sharedVariable.uiService

    this.commissionBatchOid = this.$route.query._oid
    await this.getDetailRecordsList()
    this.displayHiearchySetupColumns()
  }

  displayHiearchySetupColumns () {
    if (this.projectSettings.useHierarchySetup) {
      this.detailsRecordsColumn.push(
        { label: 'Performance Bonus', field: 'performanceBonusPayout' },
        { label: 'Service Charge', field: 'serviceChargePayout' },
        { label: 'Profit Sharing RP', field: 'profitSharingRpPayout' }
      )
    }
  }

  async getDetailRecordsList () {
    const rawRecord = await CommissionRecordsService.getCommissionRecordsByBatch(this.commissionBatchOid)
    this.detailsTableData = this.formatCommissionRecord(rawRecord)
  }

  formatCommissionRecord (rawRecords: CommissionRecordDTO []) {
    return rawRecords.map((record: CommissionRecordDTO) => {
      this.percentageKey.forEach((pctKey: string) => {
        if (record[pctKey]) {
          record[pctKey] = this.formatValue(record[pctKey], this.commissionDecimal)
        }
      })

      this.currencyKey.forEach((key: string) => {
        if (record[key]) {
          record[key] = this.formatValue(record[key], 2)
        }
      })

      return record
    })
  }

  showDetail (row: CommissionRecordDTO): void {
    this.showDetailDialog = true
    this.selectedDetail = row
  }

  mapRawKeytoFormatKey (list: string[]) {
    return list.filter((key) => { return key !== '_oid' && key !== 'ctm' }).map(key => {
      return camelCase(key)
    })
  }

  async exportCSV () {
    try {
      const commissionBatchOid = this.$route.query._oid as string
      const res = await CommissionRecordsService.getCommissionBatchCsv(commissionBatchOid)

      const blob = new Blob([res], { type: 'text/csv' })
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveBlob(blob, 'commission_batch.csv')
      } else {
        const elem = window.document.createElement('a')
        elem.href = window.URL.createObjectURL(blob)
        elem.download = 'commission_batch.csv'
        document.body.appendChild(elem)
        elem.click()
        document.body.removeChild(elem)
      }
    } catch (err) {
      alert(err.response.data.message)
    }
  }

  async saveRecord () {
    await CommissionRecordsService.updateCommissionBatch({ commissionBatchOid: this.commissionBatchOid, paid: this.commissionInfo.paid, remark: this.commissionInfo.batchRemark })
    await this.getDetailRecordsList()
  }
}
